import { Link } from "@/lib/router-compat";
import { ArrowLeft } from "lucide-react";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-foreground mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-10">Last updated: February 19, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Welcome to Sentinel Forge Systems. By accessing or using our platform, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service. These terms constitute a legally binding agreement between you and Sentinel Forge Systems.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">2. Eligibility</h2>
            <p className="text-muted-foreground leading-relaxed">
              You must be at least 18 years of age and authorized to use this service on behalf of your business or organization. By creating an account, you represent and warrant that you have the authority to bind your organization to these terms and that all registration information you provide is accurate and current.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">3. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              Sentinel Forge Systems is a Software-as-a-Service (SaaS) platform designed for the security industry. Our platform provides tools for generating security proposals, pricing calculations, risk assessments, client portal management, and related business operations tools. The service may be updated, modified, or expanded at our discretion.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">4. Account Responsibilities</h2>
            <p className="text-muted-foreground leading-relaxed">
              You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. You agree to notify us immediately of any unauthorized use of your account. We are not liable for any loss or damage arising from your failure to protect your account credentials.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">5. Subscription & Billing</h2>
            <ul className="text-muted-foreground leading-relaxed space-y-2 list-disc pl-5">
              <li>All payments are securely processed through Stripe. We do not store your payment card details on our servers.</li>
              <li>Subscription plans renew automatically at the end of each billing period unless canceled before the renewal date.</li>
              <li>No refunds will be issued for partial billing periods. You may cancel your subscription at any time, and access will continue until the end of the current billing cycle.</li>
              <li>We reserve the right to change pricing with reasonable notice. Continued use after a price change constitutes acceptance of the new pricing.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">6. Acceptable Use</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree not to misuse the platform in any way. Prohibited activities include, but are not limited to: attempting to reverse engineer, decompile, or disassemble any part of the service; using the platform for any illegal or unauthorized purpose; interfering with or disrupting the integrity or performance of the service; accessing the service through automated means (bots, scrapers) without prior written consent; and sharing your account credentials with unauthorized parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">7. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              All content, features, functionality, branding, logos, and design elements of Sentinel Forge Systems are the exclusive property of the company and are protected by intellectual property laws. You may not reproduce, distribute, or create derivative works from any part of the service without prior written permission. Content you generate using the platform (such as proposals) remains yours.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">8. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to suspend or terminate your account at any time if you violate these Terms of Service, engage in fraudulent activity, or misuse the platform in a manner that negatively impacts other users or the service. Upon termination, your right to access the service will cease immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">9. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground leading-relaxed">
              The service is provided on an "as-is" and "as-available" basis without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not guarantee that the service will be uninterrupted, error-free, or completely secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">10. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              To the maximum extent permitted by applicable law, Sentinel Forge Systems and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, arising from your use of the service. Our total liability shall not exceed the amount you paid us in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">11. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to update or modify these Terms of Service at any time. Changes will be posted on this page with an updated "Last updated" date. Your continued use of the service after any changes constitutes your acceptance of the revised terms. We encourage you to review these terms periodically.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">12. Contact Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at{" "}
              <a href="mailto:support@sentinelforgesystems.com" className="text-primary hover:underline">support@sentinelforgesystems.com</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;
